import React, {useCallback, useEffect, useRef, useState} from 'react';
import Container from 'react-bootstrap/Container';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@material-ui/core/Button';
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {invitePatient} from "../../actions/invitePatient.js";
import {
    validateDiagnoses,
    validateEmailAddress,
    validateFirstName,
    validateKeyDate,
    validateLastName,
    validateOrganization
} from "./validation.js";
import Select from "@material-ui/core/Select";
import {InputWithErrorMessage} from "../forms/inputWithErrorMessage.js";
import styles from "./patientinvite.module.scss";
import {userProfile} from "../../actions/userProfile.js";
import {getOrbitList, getUser} from "../../helpers/index.js";
import {REGISTER_ERROR} from "../../actions/types.js";
import {diagList} from "../../actions/diagList.js";
import {orgList} from "../../actions/orgList.js";
import {
    SERVICE_LINE_ADMIN,
    ORBIT_ADMIN,
    ORGANIZATION_ADMIN,
    REGULAR_USER,
    SYS_ADMIN,
    isProfessional,
    isPublicUser,
    hasRole,
    ROLE_KEY,
} from "../../constants/roles.js";
import {diagnosesList} from "../../actions/diagnosisList.js";
import {diagnosisUserList} from "../../actions/diagnosisUserList.js";
import {
    validatePassword,
    validatePhoneNumber,
    validateUserName,
} from "../auth/register/validation.js";
import { IMaskInput } from 'react-imask';
import { DayPicker } from 'react-day-picker';
import { format, isValid, parse } from 'date-fns';
import 'react-day-picker/dist/style.css';
import {generateOrbitIntegrationCode} from "../../actions/orbitIntegrationCode.js";
import {toast, ToastContainer} from "react-toastify";
import {InputAdornment} from "@material-ui/core";
import {ALREADY_ACTIVATED, RESEND_INVITE} from "../../constants/inviteAction";
import {setOrganizationList} from "../../actions/home";
import {getServiceLineList} from "../../actions/serviceLineList";
import {orbit} from "../../actions/orbit";
import {validateOrbit, validateServiceLine} from "../userinvite/validation";
import Card from "react-bootstrap/Card";
import {CardContent} from "@mui/material";
import {keyDateByOrbit} from "../../actions/keyDateByOrbit";
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';



// Phone mask component - DEFINED OUTSIDE PatientInvite
const PhoneMaskCustom = React.forwardRef(function PhoneMaskCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <IMaskInput
            {...other}
            mask="(000) 000-0000"
            definitions={{
                '0': /[0-9]/,
            }}
            inputRef={ref}
            onAccept={(value) => onChange({ target: { name: props.name, value } })}
            overwrite
        />
    );
});

// Attaches a native 'change' listener via callback ref so programmatic value
// changes (Playwright, autofill, assistive tech) update React state even when
// React 17's synthetic onChange is not triggered.
function useNativeChangeRef(handler) {
    const infoRef = useRef(null);
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    const callbackRef = useCallback((el) => {
        if (infoRef.current) {
            infoRef.current.el.removeEventListener('change', infoRef.current.fn);
            infoRef.current = null;
        }
        if (el) {
            const fn = () => handlerRef.current(el.value);
            el.addEventListener('change', fn);
            infoRef.current = { el, fn };
        }
    }, []);
    return callbackRef;
}

const PatientInvite = () => {
    const formRef = useRef(null);
    const submitButtonRef = useRef(null);
    const user = getUser();
    const [
        userTriggeredAlreadyActivatedModal,
        setUserTriggeredAlreadyActivatedModal,
    ] = useState(false);
    const [tempEmailAddress, setTempEmailAddress] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [orbitIntegrationCode, setOrbitIntegrationCode] = useState({});
    const [emailAddress, setEmailAddress] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [diagnoses, setDiagnoses] = useState("");
    const [diagnosisList, setDiagnosisList] = useState([]);
    const [organizationId, setOrganizationId] = useState("");
    const [orbitIdList, setOrbitIdList] = useState([]);
    const [selectedOrbit, setSelectedOrbit] = useState(null);
    const [organizationList, setOrganizationList] = useState([]);
    const [firstNameError, setFirstNameError] = useState("");
    const [lastNameError, setLastNameError] = useState("");
    const [emailAddressError, setEmailAddressError] = useState("");
    const [phoneNumberError, setPhoneNumberError] = useState("");
    const [diagnosesError, setDiagnosesError] = useState("");
    const [organizationIdError, setOrganizationIdError] = useState("");
    const [userMessage, setUserMessage] = useState("");
    const [sendType, setSendType] = useState("BOTH");
    const [isDirty, setIsDirty] = useState(false);
    const {errorMessage} = useSelector((state) => state.register);
    const [displayOrbitIntegrationCode, setDisplayOrbitIntegrationCode] =
        useState(false);
    const [inviteAction, setInviteAction] = useState(0);
    const [isActive, setIsActive] = useState(false);
    const [alreadyActivatedIsActive, setAlreadyActivatedIsActiveIsActive] =
        useState(false);
    const [resendInvite, setResendInvite] = useState(false);
    const [serviceLineId, setServiceLineId] = useState("");
    const [serviceLineList, setServiceLineList] = useState([]);
    const [orbitList, setOrbitList] = useState([]);
    const [orbitId, setOrbitId] = useState("");
    const orbitIdRef = useRef("");
    const orgNativeRef = useNativeChangeRef((value) => {
        setOrganizationId(prev => value !== prev ? value : prev);
    });
    const serviceLineNativeRef = useNativeChangeRef((value) => {
        setServiceLineId(prev => {
            const current = Array.isArray(prev) ? prev[0] : prev;
            return value !== current ? (value ? [value] : "") : prev;
        });
    });
    const orbitNativeRef = useNativeChangeRef((value) => {
        orbitIdRef.current = value;
        setOrbitId(prev => value !== prev ? value : prev);
    });
    const diagnosisNativeRef = useNativeChangeRef((value) => {
        setDiagnoses(prev => {
            const current = Array.isArray(prev) && prev.length > 0 ? String(prev[0]) : "";
            return value !== current ? (value ? [parseInt(value)] : []) : prev;
        });
    });
    const [serviceLineError, setServiceLineError] = useState("");
    const [orbitError, setOrbitError] = useState("");
    const [keyDates, setKeyDates] = useState([]);
    const [keyDateValues, setKeyDateValues] = useState({});
    const [keyDateInputValues, setKeyDateInputValues] = useState({});
    const [keyDateErrors, setKeyDateErrors] = useState({});
    const [openPickerId, setOpenPickerId] = useState(null);
    const [keyDateMonth, setKeyDateMonth] = useState(new Date());
    const dialogRef = useRef(null);
    const {
        defaultOrbit,
        diagnosticId
    } = useSelector(state => {
        return state.home
    });
    const [open, setOpen] = React.useState(false);
    const [alreadyActivatedOpen, setAlreadyActivatedOpen] = React.useState(false);

    const loadOrganization = () => {
        (async () => {
            const ol = await orgList({}, (data) => {
                setOrganizationList(
                    data.organizations.map((organization) => ({
                        organizationId: organization.organizationId,
                        orgName: organization.orgName,
                    })),
                );
            });

            await dispatch(ol);
        })();
    };

    const handleClick = (event) => {
        setResendInvite(true);
        setTimeout(() => {
            formRef.current.requestSubmit();
        }, 0);
    };
    const handleAlreadyActivatedClick = (event) => {
        setUserTriggeredAlreadyActivatedModal(true);
        setTempEmailAddress(emailAddress);
        setResendInvite(false);
        setPhoneNumber("");
        setEmailAddress("");
        setFirstName("");
        setLastName("");
        setOrganizationId("");
        setDiagnoses([]);
        handleAlreadyActivatedClose();
    };

    const handleOpen = () => {
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
    };

    const handleAlreadyActivatedOpen = () => {
        setAlreadyActivatedOpen(true);
    };
    const handleAlreadyActivatedClose = () => {
        setAlreadyActivatedOpen(false);
    };

    const dispatch = useDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        if (errorMessage) {
            toast.error(errorMessage);
        }
    }, [errorMessage]);

    useEffect(() => {
        if (userMessage) {
            toast.success(userMessage);
        }
    }, [userMessage]);

    const submitForm = async (e) => {
        let tempPhoneNumber = null;
        let tempEmailAddress = null;

        e.preventDefault();

        dispatch({
            type: REGISTER_ERROR,
            payload: ''
        });

        if (!(await validateAllFields())) return;

        if (!emailAddress || emailAddress.length === 0) tempEmailAddress = null;
        else tempEmailAddress = emailAddress;

        if (!phoneNumber || phoneNumber.length === 0) tempPhoneNumber = null;
        else tempPhoneNumber = phoneNumber;

        const keyDatesPayload = (keyDates || [])
            .map((kd) => {
                const value = keyDateValues[kd.keyDateId];
                if (!value) return null;
                return {
                    keyDateId: kd.keyDateId,
                    dateValue: value.format('YYYY-MM-DD')
                };
            })
            .filter(kd => kd !== null);




        const ip = await invitePatient(
            {
                diagnoses:
                    diagnoses && Array.isArray(diagnoses) && diagnoses.length > 0
                        ? diagnoses
                        : diagnoses && diagnoses.length > 0
                            ? [diagnoses]
                            : undefined,
                organizationId,
                firstName,
                lastName,
                orbitIntegrationCode: orbitIntegrationCode?.orbitIntegrationCode,
                emailAddress: tempEmailAddress,
                orbitId,
                serviceLineId:
                    serviceLineId &&
                    Array.isArray(serviceLineId) &&
                    serviceLineId.length > 0
                        ? serviceLineId[0]
                        : serviceLineId && serviceLineId.length > 0
                            ? serviceLineId
                            : undefined,
                sendType,
                isPatient: true,
                inviteMethod:
                    sendType === "Text"
                        ? "TEXT"
                        : sendType === "Email"
                            ? "EMAIL"
                            : "BOTH",
                phoneNumber:
                    tempPhoneNumber && tempPhoneNumber.length > 0
                        ? tempPhoneNumber
                        : null,
                inviteType: 3,
                userCategory: "PATIENT",
                roleKey: [ROLE_KEY.REGULAR_USER],
                resendInvite,
                keyDates: keyDatesPayload && keyDatesPayload.length > 0 ? keyDatesPayload : undefined
            },
            (data) => {
                if (data.inviteAction && data.inviteAction === RESEND_INVITE) {
                    handleOpen();
                } else if (
                    data.inviteAction &&
                    data.inviteAction === ALREADY_ACTIVATED
                ) {
                    handleAlreadyActivatedOpen();
                } else {
                    setUserMessage("Invitation Sent!");

                    setTimeout(() => {
                        navigate("/");
                    }, 3000);
                }
            }
        );
        await dispatch(ip);
    };

    useEffect(() => {
        (async () => {
        // Recover user.roles if missing (e.g. corrupted localStorage)
        if (user && !user.roles) {
            if (user.organizationId && user.organizations?.length > 0) {
                const currentOrg = user.organizations.find((org) => org.organizationId === user.organizationId);
                if (currentOrg?.roles) {
                    user.roles = currentOrg.roles;
                    setUser(user);
                }
            } else if (user.defaultOrbit && user.orbits?.length > 0) {
                const currentOrbit = user.orbits.find((o) => o.orbitId === user.defaultOrbit);
                if (currentOrbit?.roles) {
                    user.roles = currentOrbit.roles;
                    setUser(user);
                }
            }
        }

        if (!selectedOrbit && defaultOrbit && defaultOrbit?.length > 0)
            setSelectedOrbit(defaultOrbit);

        if (user?.orbits?.length === 1) {
            const findCurrentOrbit = user?.orbits?.find(
                (orbit) => orbit.orbitId === user?.defaultOrbit,
            );
            if (findCurrentOrbit) {
                setOrbitId(user?.defaultOrbit);
                setOrganizationId(findCurrentOrbit.organizationId);
                setOrbitList(
                    user?.orbits?.map((orbit) => {
                        return {
                            orbitId: orbit.orbitId,
                            orbitName: orbit.name,
                        };
                    }),
                );
            } else if (user?.organizations?.length === 1) {
                const findCurrentOrg = user?.organizations?.find(
                    (organization) => organization.organizationId === user?.organizationId,
                );
                if (findCurrentOrg) {
                    setOrganizationId(findCurrentOrg.organizationId);
                    setOrbitList(
                        user?.orbits?.map((orbit) => {
                            return {
                                orbitId: orbit.orbitId,
                                orbitName: orbit.name,
                            };
                        }),
                    );
                }
            } else if (user?.organizations?.length > 1) {
                setOrganizationId(user?.organizationId);
            }
        } else if (user?.organizations?.length === 1) {
            const findCurrentOrbit = user?.organizations?.find(
                (organization) => organization.organizationId === user?.organizationId,
            );
            if (findCurrentOrbit) {
                setOrganizationId(findCurrentOrbit.organizationId);
            }
        } else if (user?.orbits?.length > 1 && hasRole(user?.roles, ORBIT_ADMIN)) {
            setOrganizationId(user?.organizationId);
            const filteredOrbits = user?.orbits?.filter(
                (orbit) => orbit.organizationId === user?.organizationId,
            );
            setOrbitList(
                filteredOrbits?.map((orbit) => {
                    return {
                        orbitId: orbit.orbitId,
                        orbitName: orbit.name,
                    };
                }),
            );
        } else if (user?.organizations?.length > 1) {
            setOrganizationId(user?.organizationId);
        }

        if (
            user?.organizationId === "26f0cfc0-1def-11ef-8ace-f7d33dacea23" &&
            !hasRole(user?.roles, SYS_ADMIN)
        ) {
            const oic = await generateOrbitIntegrationCode((integrationCode) => {
                setOrbitIntegrationCode(integrationCode);
                setDisplayOrbitIntegrationCode(true);
            });
            await dispatch(oic);
        }

        if (!isPublicUser(user?.roles)) {
            const ol = await orgList({}, (data) => {
                setOrganizationList(
                    data?.organizations?.map((organization) => {
                        return {
                            organizationId: organization.organizationId,
                            orgName: organization.orgName,
                        };
                    }),
                );
            });
            await dispatch(ol);
        }

        if (
            hasRole(user?.roles, SERVICE_LINE_ADMIN) ||
            hasRole(user?.roles, ORBIT_ADMIN)
        ) {
            setDiagnoses(
                Array.isArray(diagnosticId)
                    ? diagnosticId
                    : diagnosticId
                        ? [diagnosticId]
                        : "",
            );
        }
        })();
    }, []);

    useEffect(() => {
        (async () => {
        if (
            organizationId === "26f0cfc0-1def-11ef-8ace-f7d33dacea23" &&
            orbitIntegrationCode?.length === 0 &&
            hasRole(user?.roles, SYS_ADMIN)
        ) {
            const oic = await generateOrbitIntegrationCode((integrationCode) => {
                setOrbitIntegrationCode(integrationCode);
                setDisplayOrbitIntegrationCode(true);
            });
            await dispatch(oic);
        }

        if (!isPublicUser(user?.roles)) {
            if (
                organizationId &&
                organizationId.length > 0 &&
                !isPublicUser(user?.roles)
            ) {
                const dl = await diagnosisUserList({organizationId}, (data) => {
                    setDiagnosisList(data.data);
                });
                await dispatch(dl);
            }
        }

        if (
            !isPublicUser(user?.roles)
        ) {
            if (
                organizationId &&
                organizationId.length > 0 &&
                !isPublicUser(user?.roles)
            ) {
                const organization = user?.organizations?.find((org) => org.organizationId === organizationId);
                const filterByList = (hasRole(user?.roles, SERVICE_LINE_ADMIN) && organization?.serviceLines?.length > 0)
                    ? organization.serviceLines
                    : undefined;

                const loadOrbitsByOrganization = async () => {
                    setServiceLineList([]);
                    const orb = await orbit({organizationId}, async (data) => {
                        setOrbitList(data?.orbits);
                    });
                    await dispatch(orb);
                };

                try {
                    const od = await getServiceLineList(
                        {organizationId, filterByList},
                        async (data) => {
                            if (
                                data &&
                                Array.isArray(data?.items) &&
                                data?.items &&
                                data?.items?.length === 1 &&
                                hasRole(user?.roles, SERVICE_LINE_ADMIN)
                            ) {
                                setServiceLineId([data.items[0].serviceLineId]);
                                setServiceLineList([]);
                            } else if (
                                data &&
                                Array.isArray(data?.items) &&
                                data?.items &&
                                data?.items?.length > 0
                            ) {
                                /*
                                if (!data.items.some(item => item["name"] === 'Do not filter'))
                                    data.items.push({serviceLineId: '', name: 'Do not filter'});
                                 */
                                setServiceLineList(data?.items);
                            } else {
                                if (hasRole(user?.roles, ORBIT_ADMIN) && user?.orbits?.length) {
                                    const filteredOrbits = user?.orbits?.filter(
                                        (orbit) => orbit.organizationId === organizationId,
                                    );
                                    if (filteredOrbits?.length > 0) {
                                        setOrbitList(
                                            filteredOrbits.map((orbit) => ({
                                                orbitId: orbit.orbitId,
                                                orbitName: orbit.name,
                                            })),
                                        );
                                    } else {
                                        await loadOrbitsByOrganization();
                                    }
                                } else {
                                    await loadOrbitsByOrganization();
                                }
                            }
                        },
                    );
                    await dispatch(od);
                } catch (e) {
                    if (hasRole(user?.roles, ORBIT_ADMIN) && user?.orbits?.length) {
                        const filteredOrbits = user?.orbits?.filter(
                            (orbit) => orbit.organizationId === organizationId,
                        );
                        if (filteredOrbits?.length > 0) {
                            setOrbitList(
                                filteredOrbits.map((orbit) => ({
                                    orbitId: orbit.orbitId,
                                    orbitName: orbit.name,
                                })),
                            );
                        } else {
                            await loadOrbitsByOrganization();
                        }
                    } else {
                        await loadOrbitsByOrganization();
                    }
                }
            }
        }
        })();
    }, [organizationId]);

    useEffect(() => {
        // Reset key date values when orbit changes
        setKeyDateValues({});
        setKeyDateInputValues({});
        setKeyDateErrors({});

        if (!orbitId || orbitId.length === 0) return;

        (async () => {
            const keyDate = await keyDateByOrbit({orbitId: orbitId}, async (data) => {
                if (orbitIdRef.current === orbitId) {
                    setKeyDates(data?.data || []);
                }
            });
            await dispatch(keyDate);
        })()
    }, [orbitId]);

    useEffect(() => {
        (async () => {
            if (serviceLineId && serviceLineId.length > 0) {
                const orb = await orbit({serviceLineId, organizationId}, async (data) => {
                    setOrbitList(data?.orbits);
                });
                await dispatch(orb);
            }
        })();
    }, [serviceLineId]);

    const changeOrbit = (e) => {
        const value = e.target.value;
        orbitIdRef.current = value;
        setOrbitId(value);
    };
    const changeFirstName = (e) => {
        setFirstName(e.target.value);
    };
    const changeLastName = (e) => {
        setLastName(e.target.value);
    };
    const changeEmailAddress = (e) => {
        setEmailAddress(e.target.value);
    };
    const changeOrganization = (e) => {
        (async () => {
            setOrganizationId(e.target.value);
            await validateOrganizationField(e);
        })();
    };
    const changeServiceLine = (e) => {
        (async () => {
            setServiceLineId([e.target.value]);
            await validateServiceLineField(e);
        })();
    };
    const changeDiagnosis = (e) => {
        (async () => {
            setDiagnoses([parseInt(e.target.value)]);
            await validateDiagnosisField(e);
        })();
    };

    const changePhoneNumber = (e) => setPhoneNumber(e.target.value);
    const changeSendType = (e) => setSendType(e.target.value);

    const changeKeyDateValue = (keyDateId, date) => {
        setKeyDateValues(prev => ({...prev, [keyDateId]: date}));
        setKeyDateInputValues(prev => ({...prev, [keyDateId]: date ? date.format('MM/DD/YYYY') : ''}));
    };

    const handleKeyDateInputChange = (keyDateId, inputValue) => {
        setKeyDateInputValues(prev => ({...prev, [keyDateId]: inputValue}));
        const parsedDate = parse(inputValue, 'MM/dd/yyyy', new Date());
        if (isValid(parsedDate)) {
            setKeyDateValues(prev => ({...prev, [keyDateId]: parsedDate}));
            setKeyDateMonth(parsedDate);
        } else {
            setKeyDateValues(prev => ({...prev, [keyDateId]: undefined}));
        }
    };

    const toggleKeyDateDialog = (keyDateId) => {
        if (openPickerId === keyDateId) {
            setOpenPickerId(null);
        } else {
            const currentDate = keyDateValues[keyDateId];
            if (currentDate) setKeyDateMonth(currentDate);
            else setKeyDateMonth(new Date());
            setOpenPickerId(keyDateId);
        }
    };

    const handleKeyDateSelect = (keyDateId, date, isRequired) => {
        changeKeyDateValue(keyDateId, date);
        setOpenPickerId(null);
        validateKeyDateField(keyDateId, date, isRequired);
    };

    const validateKeyDateField = (keyDateId, value, isRequired) => {
        setIsDirty(true);
        const error = validateKeyDate(value, isRequired);


        setKeyDateErrors(prev => ({...prev, [keyDateId]: error}));
    };

    const validateOrganizationField = async (e) => {
        if (!isPublicUser(user?.roles) && Array.isArray(organizationList) && organizationList.length > 0) {
            await setIsDirty(true);
            await setOrganizationIdError(validateOrganization(e.target.value));
        }
    };

    const validateServiceLineField = async (e) => {
        if (
            user.isSysAdmin ||
            (serviceLineList &&
                serviceLineList.length > 0 &&
                (hasRole(user?.roles, SYS_ADMIN) ||
                    hasRole(user?.roles, ORGANIZATION_ADMIN) ||
                    hasRole(user?.roles, SERVICE_LINE_ADMIN)))
        ) {
            await setIsDirty(true);
            setServiceLineError(validateServiceLine(e.target.value));
        }
    };

    const validateOrbitField = async (e) => {
        if (
            (hasRole(user?.roles, SYS_ADMIN) ||
                hasRole(user?.roles, ORGANIZATION_ADMIN) ||
                hasRole(user?.roles, SERVICE_LINE_ADMIN) ||
                ((hasRole(user?.roles, ORBIT_ADMIN) || isPublicUser(user?.roles)) && user?.orbits?.length > 1)) &&
            organizationId && organizationId.length > 0 &&
            Array.isArray(orbitList) && orbitList.length > 0
        ) {
            await setIsDirty(true);
            await setOrbitError(validateOrbit(e.target.value));
        }
    };

    const validateDiagnosisField = async (e) => {
        if (
            diagnosisList &&
            diagnosisList.length > 0 &&
            (user.isSysAdmin || isProfessional(user?.roles))
        ) {
            await setIsDirty(true);
            await setDiagnosesError(validateDiagnoses(e.target.value));
        }
    };

    const validateFirstNameField = async (e) => {
        await setIsDirty(true);
        await setFirstNameError(validateFirstName(e.target.value));
    };

    const validateLastNameField = async (e) => {
        await setIsDirty(true);
        await setLastNameError(validateLastName(e.target.value));
    };

    const validateEmailAddressField = async (e) => {
        await setIsDirty(true);

        if (
            e.target.value &&
            e.target.value.length > 0 &&
            sendType.toLowerCase() === "text"
        ) {
            await setEmailAddressError(validateEmailAddress(e.target.value));
        } else if (
            sendType.toLowerCase() === "email" ||
            sendType.toLowerCase() === "both"
        ) {
            await setEmailAddressError(validateEmailAddress(e.target.value));
        }
    };

    const validatePhoneNumberField = async (e) => {
        await setIsDirty(true);
        await setPhoneNumberError(validatePhoneNumber(e.target.value));
    };

    const validateAllFields = async () => {
        await setIsDirty(true);
        const invalidFirstName = validateFirstName(firstName);
        const invalidLastName = validateLastName(lastName);
        let invalidOrganization = "";
        let invalidDiagnosis = "";
        let invalidEmailAddress = "";
        let invalidPhoneNumber = "";
        let invalidOrbit = "";
        let invalidServiceLine = "";

        if (
            userTriggeredAlreadyActivatedModal === true &&
            emailAddress === tempEmailAddress
        ) {
            invalidEmailAddress =
                "Please enter a different email address the user you are trying to invite is already activated.";
        }

        if (!isPublicUser(user?.roles) && Array.isArray(organizationList) && organizationList.length > 0) {
            invalidOrganization = validateOrganization(organizationId);
            setOrganizationIdError(invalidOrganization);
        }

        if (
            serviceLineList &&
            serviceLineList.length > 0 &&
            (user.isSysAdmin ||
                hasRole(user?.roles, SYS_ADMIN) ||
                hasRole(user?.roles, ORGANIZATION_ADMIN) ||
                hasRole(user?.roles, SERVICE_LINE_ADMIN))
        ) {
            invalidServiceLine = validateServiceLine(serviceLineId);
            setServiceLineError(invalidServiceLine);
        }

        if (
            diagnosisList &&
            diagnosisList.length > 0 &&
            (user.isSysAdmin || isProfessional(user?.roles))
        ) {
            invalidDiagnosis = validateDiagnoses(diagnoses);
            setDiagnosesError(invalidDiagnosis);
        }

        if (
            (hasRole(user?.roles, SYS_ADMIN) ||
                hasRole(user?.roles, ORGANIZATION_ADMIN) ||
                hasRole(user?.roles, SERVICE_LINE_ADMIN) ||
                ((hasRole(user?.roles, ORBIT_ADMIN) || isPublicUser(user?.roles)) && user?.orbits?.length > 1)) &&
            organizationId && organizationId.length > 0 &&
            Array.isArray(orbitList) && orbitList.length > 0
        ) {
            invalidOrbit = validateOrbit(orbitId);
            setOrbitError(invalidOrbit);
        }

        if (sendType === "Text") {
            invalidPhoneNumber = validatePhoneNumber(phoneNumber);
            setPhoneNumberError(invalidPhoneNumber);

            if (emailAddress && emailAddress.length > 0) {
                invalidEmailAddress = validateEmailAddress(emailAddress);
                setEmailAddressError(invalidEmailAddress);
            }
        } else if (sendType === "BOTH") {
            invalidPhoneNumber = validatePhoneNumber(phoneNumber);
            setPhoneNumberError(invalidPhoneNumber);
            invalidEmailAddress = validateEmailAddress(emailAddress);
            setEmailAddressError(invalidEmailAddress);
        } else if (sendType === "Email") {
            invalidEmailAddress = validateEmailAddress(emailAddress);
            setEmailAddressError(invalidEmailAddress);
        }

        setFirstNameError(invalidFirstName);
        setLastNameError(invalidLastName);
        setEmailAddressError(invalidEmailAddress);

        let hasKeyDateErrors = false;
        const newKeyDateErrors = {};
        if (keyDates && keyDates.length > 0) {
            keyDates.forEach((keyDate) => {
                const value = keyDateValues[keyDate.keyDateId];
                const error = validateKeyDate(value, keyDate.isRequired);
                newKeyDateErrors[keyDate.keyDateId] = error;
                if (error.length > 0) hasKeyDateErrors = true;
            });
            setKeyDateErrors(newKeyDateErrors);
        }

        if (invalidFirstName.length > 0 ||
            invalidLastName.length > 0 ||
            invalidEmailAddress.length > 0 ||
            invalidPhoneNumber.length > 0 ||
            invalidOrganization.length > 0 ||
            invalidDiagnosis.length > 0 ||
            invalidServiceLine.length > 0 ||
            invalidOrbit.length > 0 ||
            hasKeyDateErrors)
            return false;
        else return true;
    };

    const sendTextTypeBool = sendType === "Text" || sendType === "BOTH";
    const sendEmailTypeBool = sendType === "Email" || sendType === "BOTH";

    return (
        <div className="w-100 gradient">
            <div className="w-100 patientInviteUI">
                <ToastContainer/>
                <Container className="registration invite">
                    <Box className="material-box" ref={formRef} component="form" onSubmit={submitForm} autoComplete="off">
                        <div className="pageContainer">
                            <h1 className="text-center title page-title">Invite a new user</h1>
                            <div className="material-box" style={{ display: displayOrbitIntegrationCode ? "inline" : "none",}}>
                                <h2 className="title">The Orbit Integration Code for this invitation is:</h2>
                                <h2 className="title">{orbitIntegrationCode?.orbitIntegrationCode}</h2>
                                <h2 className="title">Please copy the value and paste it into the Orbit Integration Code field for this patient in GTS.</h2>
                            </div>
                            { !isPublicUser(user?.roles) && (
                                <Card className="w-100 patientInviteUICard">
                                    <CardContent className="patientInviteUICardContent">
                                        <div className="card-body">
                                            <h2 className="title">Select which version of the orbit this user should receive</h2>
                                            { !isPublicUser(user?.roles) && Array.isArray(organizationList) && organizationList.length > 0 && (
                                                <div className="formInput">
                                                    <InputWithErrorMessage errorMessage={organizationIdError}>
                                                        <Select multiple={false} native={true} autoWidth={true} className="w-100" name="organization" id="organization" key="organization" label="Organization" inputRef={orgNativeRef} value={organizationId ? organizationId : ""} variant="standard" onChange={changeOrganization} onBlur={validateOrganizationField}>
                                                            <option value="" key="organization0">** Select Organization **</option>
                                                            { Array.isArray(organizationList) && organizationList?.length > 0 ? organizationList?.map((item, i) => (
                                                                <option value={item.organizationId} key={"organization" + (i + 1)}>{item.orgName}</option>
                                                            )): ""}
                                                        </Select>
                                                    </InputWithErrorMessage>
                                                </div>
                                            )}
                                            { (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN) || hasRole(user?.roles, ORBIT_ADMIN)) && organizationId && organizationId.length > 0 && Array.isArray(serviceLineList) && serviceLineList.length > 0 && (
                                                <div className="formInput">
                                                    <InputWithErrorMessage errorMessage={serviceLineError}>
                                                        <Select multiple={false} native={true} autoWidth={true} className="w-100" name="serviceLine" id="serviceLine" key="serviceLine" label="name" inputRef={serviceLineNativeRef} value={serviceLineId ? serviceLineId : ""} variant="standard" onChange={changeServiceLine} onBlur={validateServiceLineField}>
                                                            <option value="" key="serviceLine0">** Select Service Line **</option>
                                                            { Array.isArray(serviceLineList) && serviceLineList?.length > 0 ? serviceLineList?.map((item, i) => (
                                                                <option value={item.serviceLineId} key={"serviceLine" + (i + 1)}>{item.name}</option>
                                                            )) : ""}
                                                        </Select>
                                                    </InputWithErrorMessage>
                                                </div>
                                            )}
                                            { (hasRole(user?.roles, SYS_ADMIN) || hasRole(user?.roles, ORGANIZATION_ADMIN) || hasRole(user?.roles, ORBIT_ADMIN) || hasRole(user?.roles, SERVICE_LINE_ADMIN)) && ((organizationId && organizationId.length > 0) || (organizationId && organizationId.length > 0 && serviceLineId && serviceLineId.length > 0)) && Array.isArray(orbitList) && orbitList.length > 0 && (
                                                <div className="formInput">
                                                    <InputWithErrorMessage errorMessage={orbitError}>
                                                        <Select multiple={false} native={true} autoWidth={true} className="w-100" name="orbit" id="orbit" key="orbit" label="orbitName" inputRef={orbitNativeRef} value={orbitId ? orbitId : ""} variant="standard" onChange={changeOrbit} onBlur={validateOrbitField}>
                                                            <option value="" key="orbit0">** Select Orbit **</option>
                                                            { Array.isArray(orbitList) && orbitList?.length > 0 ? orbitList?.map((item, i) => (
                                                                <option value={ item.orbitId } key={ "orbit" + (i + 1) }>{item.orbitName}</option>
                                                            )): ""}
                                                        </Select>
                                                    </InputWithErrorMessage>
                                                </div>
                                            )}
                                            { diagnosisList && diagnosisList.length > 0 && isProfessional(user?.roles) && (
                                                <div className="formInput" style={{ marginBottom: "2rem"}}>
                                                    <InputWithErrorMessage errorMessage={ diagnosesError }>
                                                        <Select multiple={ false } native={ true } autoWidth={ true } className="w-100" name="diagnosis" id="diagnosis"
                                                                key="diagnosis" label="Diagnosis" inputRef={diagnosisNativeRef} value={ diagnoses ? diagnoses : "" } variant="standard" onChange={changeDiagnosis} onBlur={validateDiagnosisField}>
                                                            <option value="">** Select Version **</option>
                                                            { diagnosisList && diagnosisList.length > 0 ? diagnosisList?.map((item, i) => (
                                                                <option value={item.diagnosisId} key={"diagnosis" + i}>{item.name}</option>
                                                            )) : ""}
                                                        </Select>
                                                    </InputWithErrorMessage>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Card className="w-100 patientInviteUICard bottomCard">
                                <CardContent className="patientInviteUICardContent">
                                    <div className="card-body">
                                        <div className="w-100 material-box flex-column p-3">
                                            <h2 className="title">Choose Invitation Type</h2>
                                            <div className="formFlexContainer">
                                                <FormControl className="w-100">
                                                    <RadioGroup className="patientInviteUIRadioButtons" aria-labelledby="demo-radio-buttons-group-label" defaultValue="BOTH" value={sendType} onChange={changeSendType} id="sendType" name="radio-buttons-group">
                                                        <FormControlLabel value="Text" className="text-dark" control={<Radio className="patientInviteUIRadioButton"/>} label="Text" />
                                                        <FormControlLabel value="Email" className="text-dark" control={<Radio className="patientInviteUIRadioButton"/>} label="Email" />
                                                        <FormControlLabel value="BOTH" className="text-dark" control={<Radio className="patientInviteUIRadioButton"/>} label="Both" />
                                                    </RadioGroup>
                                                </FormControl>
                                            </div>
                                            { sendTextTypeBool && (
                                                <div className="formFlexContainer">
                                                    { sendTextTypeBool && !sendEmailTypeBool && (
                                                        <h3 className="title">New user's mobile phone number<br/>
                                                            (needed if inviting via SMS text message)</h3>
                                                    )}
                                                    { sendTextTypeBool && sendEmailTypeBool && (
                                                        <h3 className="title">New user's mobile phone number<br/>
                                                            (needed if inviting via SMS text message)</h3>
                                                    )}
                                                    <div className="">
                                                        <InputWithErrorMessage className="phoneInputNewUI" errorMessage={phoneNumberError}>
                                                            <TextField
                                                                className="w-100"
                                                                name="phone"
                                                                id="phone"
                                                                type="tel"
                                                                variant="outlined"
                                                                label={"Mobile Phone" + (sendTextTypeBool ? " *" : " (Optional)")}
                                                                value={phoneNumber}
                                                                onChange={changePhoneNumber}
                                                                onBlur={validatePhoneNumberField}
                                                                InputProps={{
                                                                    inputComponent: PhoneMaskCustom,
                                                                }}
                                                            />
                                                        </InputWithErrorMessage>
                                                    </div>
                                                </div>
                                            )}
                                            <div className="formFlexContainer">
                                                { (sendEmailTypeBool || sendTextTypeBool) && (
                                                    <div className="">
                                                        {!sendTextTypeBool && (
                                                            <h3 className="title">New user's email address</h3>
                                                        )}
                                                        {sendTextTypeBool && sendEmailTypeBool && (
                                                            <h3 className="title">New user's email address</h3>
                                                        )}
                                                        <div className="formInput">
                                                            <InputWithErrorMessage errorMessage={emailAddressError}>
                                                                <TextField className="w-100" name="email" id="email" key="email" type="email" label={"Email" + (sendEmailTypeBool ? " *" : " (Optional)")} variant="outlined" helperText="" value={emailAddress} onChange={changeEmailAddress} onBlur={validateEmailAddressField} />
                                                            </InputWithErrorMessage>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="formInput">
                                                    <InputWithErrorMessage errorMessage={firstNameError}>
                                                        <TextField className="w-100" name="firstName" id="firstName" key="firstName" label="User's First Name *" variant="outlined" helperText="" value={firstName} onChange={changeFirstName} onBlur={validateFirstNameField} />
                                                    </InputWithErrorMessage>
                                                </div>
                                                <div className="formInput">
                                                    <InputWithErrorMessage errorMessage={lastNameError}>
                                                        <TextField className="w-100" name="lastName" id="lastName" key="lastName" label="User's Last Name *" variant="outlined" helperText="" value={lastName} onChange={changeLastName} onBlur={validateLastNameField} />
                                                    </InputWithErrorMessage>
                                                </div>
                                                {keyDates && keyDates.length > 0 && (
                                                    <div className="keyDatesSection" style={{width: '100%', marginTop: '1rem', marginBottom: '1rem'}}>
                                                        <h3 className="text-center title" style={{marginBottom: '1rem'}}>Key Dates</h3>
                                                        {keyDates.map((keyDate) => (
                                                            <div className="formInput" key={keyDate.keyDateId} style={{position: 'relative'}}>
                                                                <div className={styles.dayPickerDropdown}>
                                                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                                        <DatePicker
                                                                            label={keyDate.name + (keyDate.isRequired === 1 ? ' *' : ' (Optional)')}
                                                                            value={keyDateValues[keyDate.keyDateId] || null}
                                                                            onChange={(newValue) => {
                                                                                handleKeyDateSelect(keyDate.keyDateId, newValue && newValue.isValid() ? newValue : null, keyDate.isRequired);
                                                                            }}
                                                                            renderInput={(params) => <TextField {...params} required={(keyDate.isRequired === 1 ? true : false)} className="w-100" variant="outlined" />}
                                                                        />
                                                                    </LocalizationProvider>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="buttonContainer loginButtonContainer">
                                                    <Button type="submit" variant="contained" className="loginUIComp center">Invite This User</Button>
                                                    <h2 className="text-center title patientInviteUIInstruction" style={{marginBottom: "2rem"}}>The new user will receive an invitation link to activate their guide.</h2>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </Box>
                </Container>
            </div>
            <Modal open={open} onClose={handleClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className="modalInvite">
                    <Typography id="modal-modal-title" variant="h6" component="h2" className="modalHeadline">
                        RE-INVITE USER
                        <div className="modal-form">
                            <span className="close-btn" onClick={handleClose}></span>
                        </div>
                    </Typography>
                    <Typography id="modal-modal-description" className="modalCopy">
                        This user has been invited to their guide but has not yet activated.
                        Click "Re-issue user invitation" and the invitation will be re-sent.
                        Or, select "Cancel" to go back.
                    </Typography>
                    <div className="buttonContainer loginButtonContainer sideBySideButtons">
                        <Button variant="contained" className="loginUIComp" onClick={handleClick}>RE-ISSUE USER INVITATION</Button>
                        <Button type="submit" variant="contained" className="loginUIComp" onClick={handleClose}>CANCEL</Button>
                    </div>
                </Box>
            </Modal>
            <Modal open={alreadyActivatedOpen} onClose={handleAlreadyActivatedClose} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
                <Box className="modalInvite">
                    <Typography id="modal-modal-title" variant="h6" component="h2" className="modalHeadline">
                        THIS USER HAS ALREADY BEEN ACTIVATED
                        <div className="modal-form">
                            <span className="close-btn" onClick={handleAlreadyActivatedClose}></span>
                        </div>
                    </Typography>
                    <Typography id="modal-modal-description" className="modalCopy">
                        This user's email address and/or phone number has already been sent
                        an invitation, and the user has successfully activated this orbit
                        guide. You can not send this user another invite for this guide
                        using that contact information. Please click the "Send an invitation
                        to different contact information" to send a new invitation using
                        another email address and/or phone number.
                    </Typography>
                    <div className="buttonContainer loginButtonContainer sideBySideButtons">
                        <Button variant="contained" className="loginUIComp" onClick={handleAlreadyActivatedClick}>
                            SEND AN INVITATION TO DIFFERENT CONTACT INFORMATION
                        </Button>
                    </div>
                </Box>
            </Modal>
            <div className="gradientBottom"></div>
        </div>
    );
};

export default PatientInvite;
